import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Empty, EmptyDescription, EmptyMedia } from '@/components/ui/empty'
import { StickyNote, Pencil } from 'lucide-react'
import type { Note } from '@/gen/cortado/v1/actions_pb'
import { formatTimestampWithTime } from '@/lib/date-utils'
import { formatFullName } from '@/lib/utils'
import { formatEnumName, getParentTypeColor } from '@/lib/enum-utils'
import { ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'

interface NoteListProps {
  notes: Note[]
  onEditNote?: (note: Note) => void
}

/**
 * NoteList - Reusable component for displaying a list of notes
 *
 * Features:
 * - Empty state when no notes
 * - Parent type badge showing note context (read from note.parentType)
 * - Edit button (shown on hover)
 * - Creator and updater information
 * - Timestamps
 */
export function NoteList({ notes, onEditNote }: NoteListProps) {
  if (notes.length === 0) {
    return (
      <Empty className="border border-dashed py-12">
        <EmptyMedia variant="icon">
          <StickyNote className="h-5 w-5" />
        </EmptyMedia>
        <EmptyDescription>No notes yet</EmptyDescription>
      </Empty>
    )
  }

  return (
    <div className="space-y-3">
      {notes.map((note) => {
        const createdByName = formatFullName(
          note.createdByUser?.firstName,
          note.createdByUser?.lastName,
          'Unknown'
        )
        const updatedByName = formatFullName(
          note.updatedByUser?.firstName,
          note.updatedByUser?.lastName,
          'Unknown'
        )
        const createdAt = formatTimestampWithTime(note.createdAt)
        const isUpdated = note.updatedByUser?.id && note.createdByUser?.id &&
          note.updatedByUser.id !== note.createdByUser.id

        return (
          <div
            key={note.id}
            className="rounded-lg border p-4 hover:bg-muted/50 transition-colors group"
          >
            <div className="flex gap-3">
              {/* Main content area */}
              <div className="flex-1 space-y-2">
                <div
                  className="text-sm text-foreground [&_p]:mb-2 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ul]:ml-4 [&_ol]:list-decimal [&_ol]:ml-4 [&_a]:text-primary [&_a]:underline [&_strong]:font-semibold [&_.mention]:text-primary [&_.mention]:font-medium [&_.mention]:bg-primary/10 [&_.mention]:rounded [&_.mention]:px-1 [&_.mention]:py-0.5"
                  dangerouslySetInnerHTML={{ __html: note.note }}
                />
                <div className="space-y-1 text-xs text-muted-foreground">
                  <div>
                    <span>Created by {createdByName}</span>
                  </div>
                  {isUpdated && (
                    <div>
                      <span>Updated by {updatedByName}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Right sidebar with badge, edit, and date stacked */}
              <div className="flex flex-col items-end gap-2 min-w-fit">
                <Badge variant={getParentTypeColor(note.parentType)}>
                  {formatEnumName(note.parentType, ParentTypeEnum)}
                </Badge>
                {onEditNote && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="opacity-0 group-hover:opacity-100 transition-opacity h-8 w-8 p-0"
                    onClick={() => onEditNote(note)}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                )}
                <div className="text-xs text-muted-foreground text-right">
                  {createdAt}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
